import { useCallback, useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Widget actions — each must match the expectedAction the backend passes to
// verifyTurnstile for the corresponding endpoint.
export const TURNSTILE_ACTIONS = {
  CONTACT: 'contact_form',
  CAREER: 'career_form',
} as const;

export type TurnstileAction =
  (typeof TURNSTILE_ACTIONS)[keyof typeof TURNSTILE_ACTIONS];

const TURNSTILE_LOAD_TIMEOUT_MS = 10_000;

let loadPromise: Promise<void> | null = null;

// Lazy-loads the Turnstile script (idempotent across mounts) and resolves
// once `window.turnstile` is callable, or rejects after a deadline.
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const deadline = Date.now() + TURNSTILE_LOAD_TIMEOUT_MS;

    // `script.onload` can fire before `window.turnstile` is assigned, and an
    // already-present script may still be initializing, so poll either way.
    const waitForReady = () => {
      if (window.turnstile) return resolve();
      if (Date.now() > deadline) {
        return reject(new Error('Turnstile failed to initialize'));
      }
      setTimeout(waitForReady, 50);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${TURNSTILE_SCRIPT_SRC}"]`,
    );
    if (existing) {
      waitForReady();
      return;
    }

    const script = document.createElement('script');
    script.src = `${TURNSTILE_SCRIPT_SRC}?render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = () => waitForReady();
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });

  // Don't cache a rejected promise — let a later mount retry the load.
  loadPromise.catch(() => {
    loadPromise = null;
  });

  return loadPromise;
}

type UseTurnstile = {
  containerRef: React.RefObject<HTMLDivElement>;
  token: string | null;
  ready: boolean;
  reset: () => void;
};

export function useTurnstile(action: TurnstileAction): UseTurnstile {
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const reset = useCallback(() => {
    setToken(null);
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    if (!siteKey) {
      // Misconfiguration: without a site key the widget can only render broken.
      // Fail loud in the console and keep `ready` false so the form stays
      // disabled instead of silently never producing a token.
      setReady(false);
      return;
    }

    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          callback: (t: string) => setToken(t),
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
        });
        setReady(true);
      })
      .catch(() => setReady(false));

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action]);

  return { containerRef, token, ready, reset };
}
